import axios from 'axios';
import * as XLSX from 'xlsx';
import { FIELD_BY_KEY } from './exportFields';

/**
 * Export engine: pulls a date range from GET /jobs/export/rows in small slices and builds the file.
 *
 * Gentle on the database by construction:
 *   - one DAY per request, paged by order_id (keyset) in pages of PAGE_SIZE - never a month at once;
 *   - at most CONCURRENCY requests in flight (stage's DB pool is 5 connections);
 *   - a failed request is retried with backoff; a day that still fails is REPORTED, never silently
 *     dropped - the caller decides whether to retry it or download an incomplete file.
 */
export const PAGE_SIZE = 500;
export const CONCURRENCY = 2;
const RETRY_DELAYS_MS = [800, 2000, 5000];
const REQUEST_TIMEOUT_MS = 90000;

const pad = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Every calendar day from `from` to `to`, inclusive, as yyyy-MM-dd. */
export const daysInRange = (from, to) => {
  if (!from || !to) return [];
  const out = [];
  const d = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  while (d <= end && out.length < 1000) {
    out.push(isoDay(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
};

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) signal.addEventListener('abort', () => { clearTimeout(t); reject(new DOMException('aborted', 'AbortError')); }, { once: true });
  });

const isAbort = (e) => e?.name === 'AbortError' || e?.code === 'ERR_CANCELED' || axios.isCancel?.(e);

/** Shared query params for /rows and /count. */
export const filterParams = (f) => {
  const p = { dateBasis: f.dateBasis || 'ORDER_DATE', legType: f.legType || 'DROP' };
  if (f.statuses && f.statuses.length) p.statuses = f.statuses.join(',');
  if (f.partnerId && String(f.partnerId) !== '0') p.partnerId = f.partnerId;
  if (f.payment) p.payment = f.payment;
  return p;
};

/** Order count for the estimate (one cheap COUNT on the server). Resolves to null when unavailable. */
export const countOrders = async (apiUrl, filters, signal) => {
  const res = await axios.get(`${apiUrl}shypvdriverapp/jobs/export/count`, {
    params: { fromDate: filters.fromDate, toDate: filters.toDate, ...filterParams(filters) },
    signal,
    timeout: 30000,
  });
  const d = res?.data;
  return d && d.success !== false && typeof d.data === 'number' ? d.data : null;
};

const fetchPage = async (apiUrl, day, filters, include, afterId, signal) => {
  let lastErr;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    try {
      const res = await axios.get(`${apiUrl}shypvdriverapp/jobs/export/rows`, {
        params: { fromDate: day, toDate: day, ...filterParams(filters), afterId, size: PAGE_SIZE, include: include.join(',') },
        signal,
        timeout: REQUEST_TIMEOUT_MS,
      });
      const body = res?.data;
      if (!body || body.success === false || !body.data) {
        const first = (body?.errors || [])[0];
        throw new Error(first?.errorDetails || first?.errorMessage || 'Unexpected response');
      }
      return body.data;
    } catch (e) {
      if (isAbort(e)) throw e;
      // A 400 is our request being wrong - retrying will not help.
      if (e?.response?.status === 400) throw e;
      lastErr = e;
      if (attempt < RETRY_DELAYS_MS.length) await sleep(RETRY_DELAYS_MS[attempt], signal);
    }
  }
  throw lastErr;
};

/**
 * Fetch every order for `days`. Resolves to { rowsByDay, failedDays: [{ day, error }] }.
 * onProgress receives { daysDone, daysTotal, rows, pages, activeDays, failed }.
 */
export const fetchOrders = async ({ apiUrl, days, filters, include, signal, onProgress }) => {
  const rowsByDay = {};
  const failedDays = [];
  let daysDone = 0;
  let rowsTotal = 0;
  let pages = 0;
  const active = new Set();
  const report = () =>
    onProgress && onProgress({ daysDone, daysTotal: days.length, rows: rowsTotal, pages, activeDays: [...active], failed: failedDays.length });

  const queue = [...days];
  const worker = async () => {
    while (queue.length) {
      if (signal?.aborted) return;
      const day = queue.shift();
      active.add(day);
      report();
      const dayRows = [];
      try {
        let afterId = 0;
        for (;;) {
          const page = await fetchPage(apiUrl, day, filters, include, afterId, signal);
          const rows = Array.isArray(page.rows) ? page.rows : [];
          dayRows.push(...rows);
          pages += 1;
          rowsTotal += rows.length;
          report();
          if (!page.hasMore || page.nextAfterId == null || rows.length === 0) break;
          afterId = page.nextAfterId;
        }
        rowsByDay[day] = dayRows;
      } catch (e) {
        if (isAbort(e)) { active.delete(day); return; }
        // Drop the half-read day entirely so a retry re-reads it cleanly (no partial days).
        rowsTotal -= dayRows.length;
        failedDays.push({ day, error: e?.response?.data?.errors?.[0]?.errorMessage || e?.message || 'Request failed' });
      }
      active.delete(day);
      daysDone += 1;
      report();
    }
  };

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, days.length || 1) }, worker));
  return { rowsByDay, failedDays };
};

const cellValue = (v) => (v === null || v === undefined ? '' : v);

/**
 * Header + data rows for the chosen columns. A totals row is added when asked and at least one
 * column is summable; non-number cells in those columns are ignored by the sum.
 */
export const buildTable = (rows, columns, { totals = false } = {}) => {
  const fields = columns.map((c) => FIELD_BY_KEY[c.key]).filter(Boolean);
  const cols = columns.filter((c) => FIELD_BY_KEY[c.key]);
  const header = cols.map((c) => c.label);
  const body = rows.map((r) => fields.map((f) => cellValue(f.get(r))));
  if (totals && fields.some((f) => f.total) && body.length) {
    const sums = fields.map((f, i) => (f.total
      ? Math.round(body.reduce((s, row) => s + (typeof row[i] === 'number' && !Number.isNaN(row[i]) ? row[i] : 0), 0) * 100) / 100
      : ''));
    const firstText = fields.findIndex((f) => !f.total);
    if (firstText >= 0) sums[firstText] = 'TOTAL';
    body.push(sums);
  }
  return { header, body };
};

const EXCEL_MAX_ROWS = 1000000; // a sheet holds 1,048,576; split well before it

/** Write the file and trigger the browser download. */
export const downloadTable = ({ header, body }, { format, fileName }) => {
  const safe = (fileName || 'export').replace(/[\\/:*?"<>|]+/g, '-');
  if (format === 'csv') {
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safe}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return;
  }
  const wb = XLSX.utils.book_new();
  const widths = header.map((h, i) => {
    const sample = body.slice(0, 200).reduce((m, row) => Math.max(m, String(row[i] ?? '').length), 0);
    return { wch: Math.min(48, Math.max(10, String(h).length + 2, sample + 2)) };
  });
  const parts = Math.max(1, Math.ceil(body.length / EXCEL_MAX_ROWS));
  for (let p = 0; p < parts; p++) {
    const ws = XLSX.utils.aoa_to_sheet([header, ...body.slice(p * EXCEL_MAX_ROWS, (p + 1) * EXCEL_MAX_ROWS)]);
    ws['!cols'] = widths;
    const lastCol = XLSX.utils.encode_col(Math.max(0, header.length - 1));
    ws['!autofilter'] = { ref: `A1:${lastCol}1` };
    XLSX.utils.book_append_sheet(wb, ws, parts === 1 ? 'Orders' : `Orders ${p + 1}`);
  }
  XLSX.writeFile(wb, `${safe}.xlsx`, { compression: true });
};
